/**
 * Branding-aware colour resolution for the title and end screens (V6).
 *
 * An author can set `branding.backgroundColor` to an arbitrary hex. We can't
 * know that colour at build time, so we can't hand-tune a contrasting text
 * colour the way the rest of the runtime palette is tuned. Instead we compute
 * the background's relative luminance and pick whichever of near-white /
 * near-black yields higher WCAG contrast — guaranteeing the screen's heading
 * and body stay legible regardless of the chosen colour.
 *
 * This is a runtime presentation concern, so it lives here rather than in
 * `@branchworks/shared`. The editor will add an authoring-time brand-contrast
 * warning in a later slice; this function is the runtime's safety net.
 */

/** Runtime defaults when no branding colour is supplied — the standard dark theme. */
const DEFAULT_BG = '#1A1815';
const DEFAULT_FG = '#FAF7F0';
const DEFAULT_FAINT = '#D9D2BE';

const LIGHT = '#FFFFFF';
/** Near-black: matches the runtime's darkest surface, not pure black, for a softer look. */
const DARK = '#1A1815';

export interface ResolvedBrandColours {
  /** Screen background. */
  bg: string;
  /** Primary foreground (heading, button text-on-fg). */
  fg: string;
  /** Slightly dimmer foreground for body copy — still AA where it lands. */
  faint: string;
}

function parseHex(hex: string): [number, number, number] | null {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function channelLin(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function luminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map(channelLin);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a: [number, number, number], b: [number, number, number]): number {
  const la = luminance(a);
  const lb = luminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Resolve the screen colours for an optional branding background. Falls back to
 * the runtime's dark theme when the colour is absent or unparseable.
 */
export function readableForeground(backgroundColor?: string): ResolvedBrandColours {
  if (!backgroundColor) {
    return { bg: DEFAULT_BG, fg: DEFAULT_FG, faint: DEFAULT_FAINT };
  }
  const rgb = parseHex(backgroundColor);
  if (!rgb) {
    return { bg: DEFAULT_BG, fg: DEFAULT_FG, faint: DEFAULT_FAINT };
  }
  const light = parseHex(LIGHT)!;
  const dark = parseHex(DARK)!;
  const useLight = contrast(rgb, light) >= contrast(rgb, dark);
  return useLight
    ? { bg: backgroundColor, fg: LIGHT, faint: 'rgba(255,255,255,0.82)' }
    : { bg: backgroundColor, fg: DARK, faint: 'rgba(26,24,21,0.78)' };
}
